import React from 'react'

import { Box } from '@mui/material'
import { rgba } from 'polished'

import MerchantBadge from 'components/MerchantBadge'
import { statusChipBase } from 'components/Status/Status.styles'

import { theme } from 'styles/theme'

import type { ColumnDef } from './types'

// ─── Canonical chip ────────────────────────────────────────────────────────
// A geometria vem inteira de `statusChipBase`
// (`src/components/Status/Status.styles.ts`) — mesma fonte que o `Status`.
// Nunca redefina tamanho/padding/raio aqui nem passe `fontSize` na chamada.

export type ChipTone =
  | 'neutral'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'custom'

interface CellChipProps {
  label: React.ReactNode
  tone?: ChipTone
  /** when tone='custom' */
  color?: string
  /** when tone='custom' */
  background?: string
  /**
   * Escape hatch para o tamanho da fonte. Por padrão NÃO use — o tamanho vem do
   * `statusChipBase`, fonte única da geometria dos chips de status/tipo. Passar
   * um valor aqui é justamente o que fazia os chips divergirem entre telas.
   */
  fontSize?: string | number
  /**
   * Override the chip padding. Defaults to the em-based 4px/8px (@14px) so it
   * scales with `fontSize`. Só use fora de tabela, onde o chip não precisa
   * caber numa célula (ex.: cards de canal da Central de Notificações).
   */
  padding?: string
}

const TONES: Record<
  Exclude<ChipTone, 'custom'>,
  { color: string; background: string }
> = {
  neutral: {
    color: (theme.palette as any).neutral?.[400] ?? '#6B7280',
    background: rgba((theme.palette as any).neutral?.[700] ?? '#E5E7EB', 0.3)
  },
  // matches Status `paid` (Paga) — green
  success: {
    color: (theme.palette as any).primary?.[200] ?? theme.palette.primary.main,
    background: rgba(
      (theme.palette as any).primary?.[200] ?? theme.palette.primary.main,
      0.2
    )
  },
  // matches Status `canceled`/`failed_payment` (Cancelada) — red
  error: {
    color: (theme.palette as any).error?.[300] ?? theme.palette.error.main,
    background: rgba(
      (theme.palette as any).error?.[300] ?? theme.palette.error.main,
      0.1
    )
  },
  // matches CustomerStage `lead` / Status `open_payment` (Aberta) — blue
  info: {
    color: (theme.palette as any).secondary?.[200] ?? theme.palette.info.main,
    background: rgba(
      (theme.palette as any).secondary?.[200] ?? theme.palette.info.main,
      0.1
    )
  },
  // matches Status late (Atrasada) — amber
  warning: {
    color: (theme.palette as any).alert?.[300] ?? theme.palette.warning.main,
    background: rgba(
      (theme.palette as any).alert?.[400] ?? theme.palette.warning.main,
      0.3
    )
  }
}

export const CellChip: React.FC<CellChipProps> = ({
  label,
  tone = 'neutral',
  color,
  background,
  fontSize,
  padding
}) => {
  const palette =
    tone === 'custom'
      ? {
          color: color || TONES.neutral.color,
          background: background || TONES.neutral.background
        }
      : TONES[tone]

  return (
    <Box
      component="span"
      sx={{
        ...statusChipBase,
        ...(fontSize ? { fontSize } : {}),
        ...(padding ? { padding } : {}),
        color: palette.color,
        background: palette.background
      }}
    >
      {label}
    </Box>
  )
}

export const ChipList: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <Box display="flex" gap="4px" flexWrap="wrap">
    {children}
  </Box>
)

// ─── Tag color normalizer ──────────────────────────────────────────────────
// API às vezes devolve cor sem `#` (ex.: "FF5733"). Sem o prefixo, CSS ignora.
export const normalizeTagColor = (color?: string): string | undefined => {
  if (!color) return undefined
  return color.startsWith('#') ? color : `#${color}`
}

// ─── Sim/Não chip ──────────────────────────────────────────────────────────
export const BooleanChip: React.FC<{
  value: any
  trueLabel?: string
  falseLabel?: string
}> = ({ value, trueLabel = 'Sim', falseLabel = 'Não' }) => (
  <CellChip
    tone={value ? 'success' : 'error'}
    label={value ? trueLabel : falseLabel}
  />
)

// ─── Payment type label map ────────────────────────────────────────────────
export const PAYMENT_TYPE_LABEL: Record<string, string> = {
  boleto: 'Boleto',
  pix: 'Pix',
  boleto_pix: 'Boleto/Pix',
  credit_card: 'Cartão',
  credit_card_recurring: 'Cartão recorrente',
  credit_card_installments: 'Cartão parcelado',
  direct_payment: 'Recebimento manual'
}

export const getPaymentTypeLabel = (pt: string) => {
  const key = pt.trim().toLowerCase()
  return PAYMENT_TYPE_LABEL[key] ?? pt.trim()
}

// Filter out redundant `boleto_pix` (already implied when both Boleto and Pix
// are listed). Keep order otherwise.
export const filterPaymentTypes = (pts: string[]): string[] =>
  pts.filter((pt) => pt.trim().toLowerCase() !== 'boleto_pix')

// ─── Meio de pagamento REALIZADO ───────────────────────────────────────────
// `billing.payment_type` é a lista do que a cobrança ACEITA (vem separada por
// `;`). O que de fato pagou está em `payments[]`, que a listagem `/v3/billings`
// já devolve — o encoder serializa `payments` com o `paymentable` junto e só
// descarta os status mortos. O pagamento que vale é o de status `paid`.

/** Sub-tipo do recebimento manual (`Payment::DirectPaymentType` na API). */
const DIRECT_PAYMENT_TYPE_LABEL: Record<string, string> = {
  boleto: 'Boleto',
  pix: 'Pix',
  credit_card: 'Cartão de crédito',
  cash: 'Dinheiro',
  transfer: 'Transferência',
  bonified_credit: 'Crédito bonificado',
  other: 'Outros'
}

export type PaidPayment = {
  status?: string
  payment_type?: string
  paymentable?: {
    installments?: number | null
    direct_type?: string | null
  } | null
}

/**
 * Rótulo do meio que efetivamente pagou. Segue o mesmo detalhamento do modal de
 * detalhe da cobrança: `direct_payment` mostra COMO foi recebido (dinheiro,
 * transferência…) em vez do genérico "Recebimento manual", e o parcelado diz em
 * quantas vezes.
 */
export const getPaidPaymentLabel = (payment: PaidPayment): string => {
  const type = (payment.payment_type || '').trim().toLowerCase()

  if (type === 'direct_payment') {
    const direct = (payment.paymentable?.direct_type || '').trim().toLowerCase()
    return (
      DIRECT_PAYMENT_TYPE_LABEL[direct] || PAYMENT_TYPE_LABEL.direct_payment
    )
  }

  if (type === 'credit_card_installments') {
    const times = payment.paymentable?.installments
    return times && times > 1
      ? `Cartão parcelado ${times}x`
      : 'Cartão parcelado'
  }

  return getPaymentTypeLabel(type)
}

/** Os pagamentos liquidados da cobrança. Vazio enquanto ela não foi paga. */
export const getPaidPayments = (payments?: PaidPayment[] | null) =>
  (payments || []).filter((payment) => payment?.status === 'paid')

// ─── Customer category tone map ────────────────────────────────────────────
// Mirrors `CustomerStage` colors from
// `templates/v2/Charge/components/ChargeTable/ChargeTable.styles.ts`.
export const customerCategoryTone = (category?: string): ChipTone => {
  switch ((category || '').toLowerCase()) {
    case 'active':
    case 'active_customer':
      return 'success'
    case 'lead':
      return 'info'
    case 'inactive':
    case 'inactive_customer':
      return 'warning'
    case 'churned':
      return 'error'
    case 'lost':
    case 'archived_customer':
    default:
      return 'neutral'
  }
}

// ─── Empresa (SC-10999) ────────────────────────────────────────────────────
// Nas listas agregadas da visão multi-empresas cada registro traz a empresa.
// A coluna é `groupOnly`: fora do grupo o useTableColumns a remove do catálogo.

export type RecordMerchant = {
  uuid: string
  name: string
  logo_url?: string | null
}

export function merchantColumn<
  TRow extends { merchant?: RecordMerchant | null }
>(): ColumnDef<TRow> {
  return {
    id: 'merchant',
    label: 'Empresa',
    groupOnly: true,
    // Primeira coluna da esquerda, antes do cliente: na visão do grupo a
    // empresa é o primeiro recorte de cada registro.
    locked: true,
    lockedPosition: 'start',
    defaultVisible: true,
    defaultOrder: 0,
    cell: (record) => (
      <MerchantBadge
        name={record?.merchant?.name}
        logoUrl={record?.merchant?.logo_url}
        size={24}
      />
    ),
    exportValue: (record) => record?.merchant?.name ?? null
  }
}
